import React, { useState } from 'react'
import styled from 'styled-components'
import { useStoresContext } from '../stores.context'
import { SingleStore, StoreLabels } from '../stores.props'
import {
  IconLocation,
  IconPhone,
  Typography,
  IconClock,
  IconArrowRight,
  IconPlus,
  IconMinus,
  Copy,
  Collapse,
} from '../../Common'
import { useLocalLabels } from '@sentecacommerce-theme/lib'

export const Store = ({ store }: { store: SingleStore }) => {
  const [isContentExpanded, setIsContentExpanded] = useState(false)
  const { filterByStoreName, selectedStore } = useStoresContext()

  const seeOnTheMap = (storeName: string) => () => {
    filterByStoreName(storeName)
  }

  const onContentButtonClick = () => {
    setIsContentExpanded((value) => !value)
  }

  const labels = useLocalLabels<StoreLabels>()

  return (
    <StoreContainer isSelected={selectedStore === store.name}>
      <StoreTitle>{store.name}</StoreTitle>
      <Info>
        <IconWrapper>
          <IconLocation style={{ width: '2.5rem', marginLeft: '-0.6rem' }} />
        </IconWrapper>
        <Label>{store.addressLabel}</Label>
      </Info>
      <Info>
        <IconWrapper>
          <IconClock style={{ width: '2.2rem', marginLeft: '-0.2rem' }} />
        </IconWrapper>
        <Label>
          {store.workingHoursLabel}
          {store.isTemporaryClosed && (
            <>
              {' '}
              (<Red>{labels.temporaryClosedLabel}</Red>)
            </>
          )}
        </Label>
      </Info>
      <Info>
        <IconWrapper>
          <IconPhone style={{ width: '2rem' }} />
        </IconWrapper>
        <Label>
          <a href={`tel:${store.phoneNumber}`}>{store.phoneNumber}</a>
        </Label>
      </Info>
      <Bottom store={store}>
        {store.description && (
          <BottomLink onClick={onContentButtonClick}>
            {isContentExpanded ? (
              <IconMinus className="mr-2 mt-1" style={{ width: '2rem' }} />
            ) : (
              <IconPlus className="mr-2 mt-1" style={{ width: '2rem' }} />
            )}
            {isContentExpanded ? labels.seeLessLabel : labels.seeMoreLabel}
          </BottomLink>
        )}
        <BottomLink onClick={seeOnTheMap(store.name)}>
          {labels.seeOnMapLabel}
          <IconArrowRight className="mt-1 ml-2" style={{ width: '0.8rem' }} />
        </BottomLink>
      </Bottom>
      <Collapse expanded={isContentExpanded}>
        <Content>
          <Copy html={store.description} />
        </Content>
      </Collapse>
    </StoreContainer>
  )
}

const StoreContainer = styled.article.attrs({
  className: 'p-4',
})<{ isSelected?: boolean }>`
  background: ${(props) =>
    props.isSelected ? props.theme.palette.panache : 'transparent'};
`

const Red = styled.span`
  color: ${({ theme }) => theme.palette.danger};
`

const StoreTitle = styled(Typography).attrs({
  as: 'h2',
  variant: 3,
  color: 'greyTitle',
  className: 'pb-3',
})`
  font-weight: normal;
  line-height: 1.2;
`

const Info = styled.div.attrs({
  className: 'd-flex mt-3',
})`
  max-width: 40rem;
`

const Label = styled(Typography).attrs({
  className: 'ml-2',
  as: 'p',
  color: 'greyTitle',
  variant: 5,
})`
  & > a {
    color: ${({ theme }) => theme.palette.silverTree};
    &:hover {
      opacity: 0.6;
    }
  }
`

const IconWrapper = styled.div.attrs({
  className: 'mt-2',
})`
  min-width: 2.5rem;
  color: ${({ theme }) => theme.palette.silverTree};
`

const Bottom = styled.div.attrs<{ store: any }>((props) => ({
  className: `d-flex align-items-center ${
    props.store.description ? `justify-content-between` : `justify-content-end`
  } mt-4`,
}))<{ store: any }>``

const BottomLink = styled(Typography).attrs({
  as: 'span',
  color: 'silverTree',
  variant: 5,
  className: 'd-flex align-items-center',
})<any>`
  cursor: pointer;

  &:hover {
    opacity: 0.6;
  }
`

const Content = styled.div.attrs({
  className: 'pt-5',
})``
